import axios from 'axios';
import { useMemo, type JSX, type PropsWithChildren } from 'react';
import {
  AttachmentsApi,
  EntityNamesApi,
  EntityTitlesApi,
  FlattenedIssueTypesApi,
  GovmapGroupsApi,
  GrcObjectsApi,
  IssueActionCountApi,
  IssueRelationshipCountApi,
  IssueSlasApi,
  IssueSubtypesApi,
  IssueTemplatesApi,
  IssueTypesApi,
  IssuesApi,
  MonitoringApi,
  RelationshipsApi,
  SlaSettingsApi,
  SlasApi,
  UsersApi,
} from '../generated/api';
import { ApiErrorRecovery, createApiErrorHandler } from './api-error-handler';
import { ApiContext, ApiContextType } from './api.context';

type AxiosProviderProps = {
  apiUrl: string;
  attachmentsApiUrl?: string;
  notifyEntitlementError: () => void;
  recover: ApiErrorRecovery;
};

export function ApiProvider({
  recover,
  notifyEntitlementError,
  apiUrl,
  attachmentsApiUrl = apiUrl,
  children,
}: PropsWithChildren<AxiosProviderProps>): JSX.Element {
  const api = useMemo((): ApiContextType => {
    const axiosClient = axios.create({
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    axiosClient.interceptors.response.use(
      (response) => {
        if (response.data) handleDates(response.data);
        return response;
      },
      createApiErrorHandler({
        axiosClient,
        recover,
        notifyEntitlementError,
      }),
    );
    return {
      axios: axiosClient,
      issuesApi: new IssuesApi(undefined, apiUrl, axiosClient),
      issueTypesApi: new IssueTypesApi(undefined, apiUrl, axiosClient),
      issueSubtypesApi: new IssueSubtypesApi(undefined, apiUrl, axiosClient),
      flattenedIssueTypesApi: new FlattenedIssueTypesApi(
        undefined,
        apiUrl,
        axiosClient,
      ),

      usersApi: new UsersApi(undefined, apiUrl, axiosClient),
      issueTemplatesApi: new IssueTemplatesApi(undefined, apiUrl, axiosClient),
      issueSLAsApi: new IssueSlasApi(undefined, apiUrl, axiosClient),
      issueEntityNamesApi: new EntityNamesApi(undefined, apiUrl, axiosClient),
      issueActionCountApi: new IssueActionCountApi(
        undefined,
        apiUrl,
        axiosClient,
      ),
      issueRelationshipCountApi: new IssueRelationshipCountApi(
        undefined,
        apiUrl,
        axiosClient,
      ),
      slaSettingsApi: new SlaSettingsApi(undefined, apiUrl, axiosClient),
      slasApi: new SlasApi(undefined, apiUrl, axiosClient),
      govmapGroupsApi: new GovmapGroupsApi(undefined, apiUrl, axiosClient),
      attachmentsApi: new AttachmentsApi(
        undefined,
        attachmentsApiUrl,
        axiosClient,
      ),
      monitoringApi: new MonitoringApi(undefined, apiUrl, axiosClient),
      entityTitlesApi: new EntityTitlesApi(undefined, apiUrl, axiosClient),
      relationshipsApi: new RelationshipsApi(undefined, apiUrl, axiosClient),
      grcObjectsApi: new GrcObjectsApi(undefined, apiUrl, axiosClient),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, attachmentsApiUrl]);

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <ApiContext.Provider value={api}>{children}</ApiContext.Provider>
  );
}

const isoDateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/;

function isIsoDateString(value: unknown): value is string {
  return (
    value !== undefined &&
    typeof value === 'string' &&
    isoDateFormat.test(value)
  );
}

function handleDates(body: Record<string, unknown>) {
  if (body === null || body === undefined || typeof body !== 'object') {
    return;
  }

  for (const [key, value] of Object.entries(body)) {
    if (isIsoDateString(value)) {
      // eslint-disable-next-line no-param-reassign
      body[key] = new Date(value);
    } else if (typeof value === 'object') {
      handleDates(value as Record<string, unknown>);
    }
  }
}
